import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import Home from './Home';
import { useStore } from '../context/StoreContext';

const VALID_TEMPLATES = ['classic', 'fashion', 'handmade', 'food', 'tech'];

// Demo tenant slugs used by the backend tenant resolver.
const TEMPLATE_TENANT_SLUGS = {
  classic: 'urdemo1',
  fashion: 'urdemo2',
  handmade: 'urdemo3',
  food: 'urdemo4',
  tech: 'urdemo5',
};

function TemplatePreview() {
  const { t } = useTranslation('common');
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { setTemplateId, refreshSettings } = useStore();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!VALID_TEMPLATES.includes(templateId)) return;

    const slug = TEMPLATE_TENANT_SLUGS[templateId];
    sessionStorage.setItem('preview_tenant_slug', slug);
    setTemplateId(templateId);
    refreshSettings();
    setReady(true);

    // In case of hard navigation, do a best-effort cleanup.
    const cleanup = () => {
      try {
        sessionStorage.removeItem('preview_tenant_slug');
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('pagehide', cleanup);
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('pagehide', cleanup);
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
      setTimeout(() => refreshSettings(), 50);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  if (!VALID_TEMPLATES.includes(templateId)) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography variant="h5" gutterBottom>
          {t('preview.not_found')}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/landing')}>
          {t('preview.back_to_landing')}
        </Button>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        data-testid="template-preview-bar"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: 'rgba(15, 30, 46, 0.95)',
          backdropFilter: 'blur(10px)',
          color: 'white',
          py: 1.2,
          px: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <VisibilityIcon sx={{ fontSize: 20, opacity: 0.75 }} />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {t('preview.label', { name: t(`preview.template_${templateId}`, templateId) })}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/apply?plan=free&template=${encodeURIComponent(templateId)}`)}
              sx={{
                bgcolor: '#D6A75B',
                color: '#0F1E2E',
                fontWeight: 950,
                borderRadius: 2,
                '&:hover': { bgcolor: '#c4963e' },
              }}
            >
              {t('preview.use_template')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/landing')}
              data-testid="template-preview-back"
              sx={{
                borderColor: 'rgba(255,255,255,0.28)',
                color: 'white',
                fontWeight: 900,
                borderRadius: 2,
                '&:hover': { borderColor: 'rgba(255,255,255,0.48)', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {t('preview.back_to_landing')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ pt: '52px' }}>
        <Layout>
          <Home />
        </Layout>
      </Box>
    </Box>
  );
}

export default TemplatePreview;
